/** Default platform display timezone (Buenos Aires). */
export const DEFAULT_DISPLAY_TIME_ZONE = 'America/Argentina/Buenos_Aires';

/** Roles super admins may delegate company timezone management to. */
export const TIMEZONE_DELEGATE_ROLE_OPTIONS = ['company_admin', 'developer'];

export const COMMON_TIME_ZONES = [
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (Argentina)' },
  { id: 'America/Sao_Paulo', label: 'São Paulo (Brazil)' },
  { id: 'America/New_York', label: 'New York (US Eastern)' },
  { id: 'America/Chicago', label: 'Chicago (US Central)' },
  { id: 'America/Denver', label: 'Denver (US Mountain)' },
  { id: 'America/Los_Angeles', label: 'Los Angeles (US Pacific)' },
  { id: 'Europe/London', label: 'London (UK)' },
  { id: 'Europe/Madrid', label: 'Madrid (Spain)' },
  { id: 'UTC', label: 'UTC' },
];

/**
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function normalizePlatformSettings(raw) {
  const delegates = Array.isArray(raw?.timezoneDelegateRoles)
    ? raw.timezoneDelegateRoles.filter(
        (r) => typeof r === 'string' && TIMEZONE_DELEGATE_ROLE_OPTIONS.includes(r),
      )
    : ['company_admin'];

  const tz =
    typeof raw?.displayTimeZone === 'string' && raw.displayTimeZone.trim()
      ? raw.displayTimeZone.trim()
      : DEFAULT_DISPLAY_TIME_ZONE;

  return {
    displayTimeZone: tz,
    timezoneDelegateRoles: [...new Set(delegates)],
  };
}

/**
 * @param {string | null | undefined} role
 * @param {{ timezoneDelegateRoles?: string[] } | null | undefined} platformSettings
 */
export function canManageTimezone(role, platformSettings) {
  if (role === 'super_admin') return true;
  const delegates = platformSettings?.timezoneDelegateRoles || [];
  return Boolean(role && delegates.includes(role));
}

/**
 * @param {{ displayTimeZone?: string } | null | undefined} platformSettings
 * @param {{ displayTimeZone?: string | null } | null | undefined} companyWorkspace
 */
export function resolveDisplayTimeZone(platformSettings, companyWorkspace) {
  const companyTz = companyWorkspace?.displayTimeZone;
  if (typeof companyTz === 'string' && companyTz.trim()) return companyTz.trim();
  return platformSettings?.displayTimeZone || DEFAULT_DISPLAY_TIME_ZONE;
}

/**
 * @param {unknown} value
 * @returns {Date | null}
 */
export function parseTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {unknown} value
 * @param {string} timeZone
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatDateTimeInZone(value, timeZone, options = {}) {
  const d = parseTimestamp(value);
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
      ...options,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * @param {string} timeZone
 * @returns {string} e.g. "GMT-3" or "GMT+5:30"
 */
export function formatGmtOffset(timeZone) {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    const raw = part?.value || '';
    if (raw === 'GMT' || raw === 'UTC') return 'GMT+0';
    return raw;
  } catch {
    return '';
  }
}

/**
 * @param {{ id: string, label: string }} zone
 * @returns {string}
 */
export function formatTimezoneMenuLabel(zone) {
  const offset = formatGmtOffset(zone.id);
  return offset ? `${offset} · ${zone.label}` : zone.label;
}

/**
 * @param {string} timeZone
 */
export function timeZoneDisplayName(timeZone) {
  const match = COMMON_TIME_ZONES.find((z) => z.id === timeZone);
  if (match) return match.label;
  try {
    const part = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: 'long',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value || timeZone;
  } catch {
    return timeZone;
  }
}
